import { Component, Vue } from "vue-property-decorator";
import NetSuiteService from "../../services/netsuite_service";
import { namespace } from "vuex-class";
import { StoreModule } from "../../store/types";
import { UserGetters } from "../../store/modules/user/types";
import {
  NetSuiteCustomerAddress,
  PartialNetSuiteCustomer,
  PriceLevel
} from "../../models/netsuite/netsuite";
import { User } from "../../models/user/user";
import { OrderActions } from "../../store/modules/order/types";
import {
  GlobalActions,
  GlobalGetters
} from "../../store/modules/global/types";
import { makeTitleCase, priceLevelMultiplier } from "../../utility/helpers";

@Component
export class CustomerMixin extends Vue {
  @(namespace(StoreModule.User).Getter(UserGetters.GetUser))
  user!: User;
  @(namespace(StoreModule.Global).Getter(GlobalGetters.GetRole))
  userRole!: string;
  @(namespace(StoreModule.Order).Action(OrderActions.ClearOrder))
  resetOrder!: () => Promise<void>;
  @(namespace(StoreModule.Order).Action(OrderActions.ApplyQuoteMultiplier))
  applyQuoteMultiplier!: (value: number) => Promise<void>;
  @(namespace(StoreModule.Global).Getter(GlobalGetters.GetPriceLevels))
  storePriceLevels!: PriceLevel[];
  @(namespace(StoreModule.Global).Getter(GlobalGetters.GetCustomers))
  storeCustomers!: PartialNetSuiteCustomer[];
  @(namespace(StoreModule.Global).Action(GlobalActions.AddMultiplier))
  addMultiplier!: (value: PriceLevel | null) => Promise<void>;
  @(namespace(StoreModule.Global).Action(GlobalActions.AddCustomers))
  addCustomers!: (value: PartialNetSuiteCustomer[]) => Promise<void>;
  @(namespace(StoreModule.Order).Action(OrderActions.AssignCustomer))
  assignCustomer!: (value: {
    customer_id: string;
    customer_name: string;
    customer_netsuite_id: number | undefined;
    customer_netsuite_contact_id: number | undefined;
    customer_netsuite_payment_terms_id: number | undefined;
    customer_netsuite_price_level_id: number | undefined;
    customer_netsuite_price_level_discount: number | undefined;
    order_id: string | null;
  }) => Promise<void>;

  protected selected: {
    customer_id: string;
    customer_name: string;
    customer_netsuite_id: number | undefined;
    customer_netsuite_contact_id: number | undefined;
    customer_netsuite_payment_terms_id: number | undefined;
    customer_netsuite_price_level_id: number | undefined;
    customer_netsuite_price_level_discount: number | undefined;
    multiplier: number;
  } | null = null;

  protected makeTitleCase = makeTitleCase;
  protected netSuiteService = new NetSuiteService();
  protected searchTerm = "";
  protected customers: PartialNetSuiteCustomer[] = [];
  protected showModal = false;

  /** Filter customers by company_name search term */
  get filteredCustomers(): PartialNetSuiteCustomer[] {
    if (!this.customers) return [];
    if (this.searchTerm !== "") {
      return this.customers.filter(
        c =>
          c.company_name
            .toLowerCase()
            .indexOf(this.searchTerm.toLowerCase()) !== -1
      );
    }
    return this.customers;
  }

  protected async getCustomers(forceRefresh = false): Promise<void> {
    if (!forceRefresh && this.storeCustomers?.length) {
      this.customers = this.storeCustomers;
      return;
    }
    const fetched = await this.netSuiteService.getCustomersForRole(
      this.userRole
    );
    await this.addCustomers(fetched);
    this.customers = fetched;
  }

  protected setSelectedCustomer(
    customerId: string,
    customerName: string,
    customerMultiplier: number,
    customerNetSuiteId: number | undefined,
    customerNetSuitePaymentTermsId: number | undefined,
    customerNetSuitePriceLevelId: number | undefined,
    customerNetSuitePriceLevelDiscount: number | undefined
  ): void {
    this.selected = {
      customer_id: customerId,
      customer_name: customerName,
      multiplier: customerMultiplier,
      customer_netsuite_id: customerNetSuiteId,
      customer_netsuite_contact_id: undefined,
      customer_netsuite_payment_terms_id: customerNetSuitePaymentTermsId,
      customer_netsuite_price_level_id: customerNetSuitePriceLevelId,
      customer_netsuite_price_level_discount: customerNetSuitePriceLevelDiscount
    };
  }

  protected async clearOrder(): Promise<void> {
    await this.resetOrder();
    await this.reassignQuote(
      this.selected?.customer_id!,
      this.selected?.customer_name!,
      this.selected?.multiplier!,
      this.selected?.customer_netsuite_id!,
      this.selected?.customer_netsuite_payment_terms_id,
      this.selected?.customer_netsuite_price_level_id!,
      this.selected?.customer_netsuite_price_level_discount
    );
  }

  protected async reassignQuote(
    customerId?: string,
    customerName?: string,
    customerMultiplier?: number,
    customerNetSuiteId?: number,
    customerNetSuitePaymentTermsId?: number,
    customerNetSuitePriceLevelId?: number,
    customerNetSuitePriceLevelDiscount?: number,
    orderId?: string
  ) {
    const id = customerId ? customerId : this.selected?.customer_id!;
    const name = customerName ? customerName : this.selected?.customer_name!;
    const multiplier = customerMultiplier
      ? customerMultiplier
      : this.selected?.multiplier!;
    const netsuite_id = customerNetSuiteId
      ? customerNetSuiteId
      : this.selected?.customer_netsuite_id;
    const netsuite_payment_terms_id =
      customerNetSuitePaymentTermsId !== undefined
        ? customerNetSuitePaymentTermsId
        : this.selected?.customer_netsuite_payment_terms_id;
    const netsuite_price_level_id = customerNetSuitePriceLevelId
      ? customerNetSuitePriceLevelId
      : this.selected?.customer_netsuite_price_level_id;
    const netsuite_price_level_discount =
      customerNetSuitePriceLevelDiscount !== undefined
        ? customerNetSuitePriceLevelDiscount
        : this.selected?.customer_netsuite_price_level_discount;
    const oid = orderId ? orderId : null;
    await this.saveCustomerToStore(
      id,
      name,
      multiplier,
      netsuite_id,
      netsuite_payment_terms_id,
      netsuite_price_level_id,
      netsuite_price_level_discount,
      oid
    );
    this.close(); // sets selected to null, must be called last
  }

  protected async saveCustomerToStore(
    id: string,
    name: string,
    multiplier: number,
    netsuite_id: number | undefined,
    netsuite_payment_terms_id: number | undefined,
    netsuite_price_level_id: number | undefined,
    netsuite_price_level_discount: number | undefined,
    oid: string | null
  ): Promise<void> {
    await this.assignCustomer({
      customer_id: id,
      customer_name: name,
      customer_netsuite_id: netsuite_id,
      customer_netsuite_contact_id: undefined,
      customer_netsuite_payment_terms_id: netsuite_payment_terms_id,
      customer_netsuite_price_level_id: netsuite_price_level_id,
      customer_netsuite_price_level_discount: netsuite_price_level_discount,
      order_id: oid
    });
    // Base Price is the fallback for a customer with no level AND for one whose level
    // is not submittable (Custom, filtered out of `storePriceLevels`) — a miss on the
    // id lookup must not leave the quote with no multiplier at all.
    const priceLevel =
      (netsuite_price_level_id
        ? this.storePriceLevels.find(l => l.id === netsuite_price_level_id)
        : null) ??
      this.storePriceLevels.find(l => l.name === "Base Price") ??
      null;
    await this.addMultiplier(priceLevel);
    // FUPOF-191: the new customer's level is written onto the lines here rather
    // than left to `Cart.loadCartPriceLevels` on the next visit. That stamp no
    // longer touches a deliberately mixed cart (it was erasing the very mix the
    // multiplier warning asks the rep to review), so re-pricing a kept cart has
    // to happen at the moment of the change — which is what this is: the rep
    // moved the quote to a customer whose price level is the one that applies.
    // Only with a level actually in hand: an empty `storePriceLevels` (not yet
    // fetched) resolves to null, and stamping that would silently re-price the
    // whole cart at x1.00.
    if (priceLevel) {
      await this.applyQuoteMultiplier(priceLevelMultiplier(priceLevel.discountpct));
    }
  }

  protected close(): void {
    this.showModal = false;
    this.selected = null;
  }

  /**
   * Select the best address from a NetSuiteCustomer's addresses array:
   * default billing > default shipping > first
   */
  protected selectAddress(
    addresses: NetSuiteCustomerAddress[]
  ): NetSuiteCustomerAddress {
    if (addresses.length === 1) return addresses[0];
    return (
      addresses.find(a => a.is_default_billing) ??
      addresses.find(a => a.is_default_shipping) ??
      addresses[0]
    );
  }

  /**
   * Derive a multiplier from price_level_discount.
   * price_level_discount is always a negative number (e.g. -0.15 for 15% off).
   */
  protected deriveMultiplier(price_level_discount: number): number {
    return priceLevelMultiplier(price_level_discount);
  }
}
